import { Component, Input, Output, EventEmitter, OnInit, OnChanges, forwardRef, ViewChild, ElementRef, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { CdkOverlayOrigin, ConnectedPosition, OverlayModule } from '@angular/cdk/overlay';
import { SearchOption } from '../../interfaces/searchoption.model';
import { GENERIC_SEARCH_CONSTANTS, UI_CONSTANTS } from '../../constants/claims-ui-constants';

export type SearchSelection = SearchOption | null;

@Component({
  selector: 'emma-generic-search',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    OverlayModule
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => GenericSearchComponent),
      multi: true
    }
  ],
  templateUrl: './generic-search.component.html',
  styleUrls: ['./generic-search.component.scss']
})
export class GenericSearchComponent implements OnInit, OnChanges, ControlValueAccessor {
  @ViewChild('searchInput') public searchInput!: ElementRef<HTMLInputElement>;
  public readonly UI_CONSTANTS = UI_CONSTANTS;
  public readonly GENERIC_SEARCH_CONSTANTS = GENERIC_SEARCH_CONSTANTS;
  @Input() public items: Array<SearchOption> = [];
  @Input() public placeholder: string = GENERIC_SEARCH_CONSTANTS.DEFAULT_PLACEHOLDER;
  @Input() public searchPlaceholder: string = UI_CONSTANTS.PLACEHOLDERS.SEARCH_CLAIMS;
  @Input() public bindLabel: string = GENERIC_SEARCH_CONSTANTS.DEFAULT_BIND_LABEL;
  @Input() public bindValue: string = GENERIC_SEARCH_CONSTANTS.DEFAULT_BIND_VALUE;
  @Input() public clearable = true;
  @Input() public disabled = false;
  @Input() public minTermLength: number = GENERIC_SEARCH_CONSTANTS.DEFAULT_MIN_TERM_LENGTH;
  @Input() public showCode = false;
  @Input() public required = false;
  @Input() public label = '';
  @Input() public showAsDropdown = false;
  @Input() public attachToBody = false;
  @Input() public isLoading = false;
  @Input() public showNoResultsMessage = true;
  @Input() public tooltip = '';
  @Input() public prefixLabelOnSelection = false;
  @Input() public suppressGuidDisplay = false;
  @Output() public searchEvent = new EventEmitter<string>();
  @Output() public selectEvent = new EventEmitter<SearchSelection>();
  @Output() public clearEvent = new EventEmitter<void>();
  @Output() public enterEvent = new EventEmitter<void>();
  @Output() public clickEvent = new EventEmitter<void>();

  public filteredItems: Array<SearchOption> = [];
  public selectedValue: SearchOption | null = null;
  public displayValue = '';
  public searchTerm = '';
  public showDropdown = false;
  public showErrors = false;
  public highlightedIndex = -1;
  private searchClearedOnOpen = false;

  @ViewChild(CdkOverlayOrigin) private readonly overlayOrigin?: CdkOverlayOrigin;
  public overlayWidth = 0;
  public readonly overlayPositions: Array<ConnectedPosition> = [
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top' }
  ];

  private onChange = (value: any) => {};
  private onTouched = () => {};

  @HostListener('document:click', ['$event'])
  public onDocumentClick(event: Event): void {
    const target = event.target as HTMLElement;
    if (this.showAsDropdown) {
      if (!target.closest(GENERIC_SEARCH_CONSTANTS.CSS_SELECTORS.DROPDOWN_CONTAINER) &&
          !target.closest(GENERIC_SEARCH_CONSTANTS.CSS_SELECTORS.DROPDOWN_CONTENT)) {
        this.showDropdown = false;
      }
    } else if (!this.searchInput?.nativeElement.contains(target) &&
          !target.closest(GENERIC_SEARCH_CONSTANTS.CSS_SELECTORS.SEARCH_DROPDOWN)) {
        this.showDropdown = false;
      }
  }

  public ngOnInit(): void {
    this.filteredItems = [...this.items];
    this.updateDisplayValueFromItems();
  }

  public ngOnChanges(): void {
    this.filteredItems = [...this.items];
    this.updateDisplayValueFromItems();
    if (this.searchTerm && this.filteredItems.length > 0) {
      setTimeout(() => {
        const firstEnabledIndex = this.filteredItems.findIndex(item => !item['disabled']);
        this.highlightedIndex = firstEnabledIndex >= 0 ? firstEnabledIndex : -1;
      }, GENERIC_SEARCH_CONSTANTS.FOCUS_TIMEOUT_DELAY);
    }
  }

  private updateDisplayValueFromItems(): void {
    if (this.selectedValue && this.items.length > 0) {
      const foundOption = this.items.find(item => this.selectedValue && this.getOptionValue(item) === this.getOptionValue(this.selectedValue));
      if (foundOption) {
        this.selectedValue = foundOption;
        const rawLabel = this.getOptionLabel(foundOption);
        const baseLabel = (this.label || '').replaceAll('*', '').trim();
     if (this.prefixLabelOnSelection && baseLabel && rawLabel) {
          this.displayValue = `${baseLabel}:${rawLabel}`;
        } else {
          this.displayValue = rawLabel;
        }
        if (!this.showAsDropdown && !this.searchClearedOnOpen) {
          this.searchTerm = this.getDisplayText(foundOption);
        }
      }
    }
    this.searchClearedOnOpen = false;
  }

  public onInputChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.searchTerm = target.value;
    this.displayValue = target.value;
    
    this.filterItems(this.searchTerm);
    this.showDropdown = this.searchTerm.length >= this.minTermLength;
    if (this.filteredItems.length > 0 && !this.filteredItems[0]['disabled']) {
      this.highlightedIndex = 0;
    } else {
      this.highlightedIndex = -1;
    } 
    if (this.selectedValue && this.getDisplayText(this.selectedValue) !== this.searchTerm) {
      this.selectedValue = null;
      this.onChange(null);
    }
    this.searchEvent.emit(this.searchTerm);
  }

  public onInputClick(): void {
    if (!this.disabled) {
      if (this.showAsDropdown) {
        this.showDropdown = !this.showDropdown;
        if (this.showDropdown) {
          this.overlayWidth = this.overlayOrigin?.elementRef.nativeElement.offsetWidth ?? 0;
          if (this.filteredItems.length === 0) {
            this.filteredItems = [...this.items];
          }
          this.searchTerm = '';
          this.searchClearedOnOpen = true;
        }
        this.highlightedIndex = -1;
        if (this.filteredItems.length === 0) {
          this.filteredItems = [...this.items];
        }
        if (this.showDropdown) {
          setTimeout(() => {
            const searchInput = document.querySelector(GENERIC_SEARCH_CONSTANTS.CSS_SELECTORS.DROPDOWN_SEARCH) as HTMLInputElement;
            if (searchInput) {
              searchInput.focus();
            }
          }, GENERIC_SEARCH_CONSTANTS.FOCUS_TIMEOUT_DELAY);
        }
      } else {
        this.showDropdown = true;
        this.searchTerm = '';
        this.searchClearedOnOpen = true;
        this.highlightedIndex = -1;
        if (this.filteredItems.length === 0) {
          this.filteredItems = [...this.items];
        }
      }
      this.clickEvent.emit();
    }
  }

  public onInputFocus(): void {
    if (!this.disabled) {
      this.showDropdown = true;
      this.showErrors = false;
      this.searchTerm = '';
      this.searchClearedOnOpen = true;
      this.highlightedIndex = -1;
    }
  }

  public onInputBlur(event: FocusEvent): void {
    setTimeout(() => {
      const relatedTarget = event.relatedTarget as HTMLElement;
      if (!relatedTarget?.closest(GENERIC_SEARCH_CONSTANTS.CSS_SELECTORS.SEARCH_DROPDOWN)) {
        this.showDropdown = false;
        this.showErrors = this.required && !this.selectedValue;
        this.onTouched();
        if (!this.selectedValue) {
          this.displayValue = '';
        }
        this.highlightedIndex = -1;
      }
    }, GENERIC_SEARCH_CONSTANTS.BLUR_TIMEOUT_DELAY);
  }

  public onKeyDown(event: KeyboardEvent): void {
    if (!this.showDropdown) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.highlightedIndex = Math.min(this.highlightedIndex + 1, this.filteredItems.length - 1);
        break;
      
      case 'ArrowUp':
        event.preventDefault();
        this.highlightedIndex = Math.max(this.highlightedIndex - 1, -1);
        break;
      
      case 'Enter':
        event.preventDefault();
        if (this.highlightedIndex >= 0 && this.filteredItems[this.highlightedIndex]) {
          const selectedOption = this.filteredItems[this.highlightedIndex];
          if (!selectedOption['disabled']) {
            this.selectOption(selectedOption);
          }
        } else if (this.filteredItems.length === 1 && !this.filteredItems[0]['disabled']) {
          this.selectOption(this.filteredItems[0]);
        } else {
          this.enterEvent.emit();
        }
        break;
      
      case 'Escape':
        event.preventDefault();
        this.showDropdown = false;
        this.highlightedIndex = -1;
        break;
    }
  }

  private filterItems(term: string): void {
    if (term.length < this.minTermLength) {
      this.filteredItems = this.minTermLength === 0 ? [...this.items] : [];
      return;
    }

    this.filteredItems = this.items.filter(item => {
      const nameValue = this.getOptionLabel(item);
      const nameMatch = nameValue?.toLowerCase().includes(term.toLowerCase());
      const codeMatch = item.code?.toLowerCase().includes(term.toLowerCase());
      return nameMatch || codeMatch;
    });
  }

  public selectOption(option: SearchOption): void {
    if (option['disabled']) {
      return;
    }
    this.selectedValue = option;
    const rawLabel = this.getOptionLabel(option);
    const baseLabel = (this.label || '').replaceAll('*', '').trim();
  if (this.prefixLabelOnSelection && baseLabel && rawLabel) {
      this.displayValue = `${baseLabel}:${rawLabel}`;
    } else {
      this.displayValue = rawLabel;
    }
    if (this.showAsDropdown) {
      this.searchTerm = '';
    } else {
      this.searchTerm = this.getDisplayText(option);
    }
    this.selectEvent.emit(option);
    const controlValue = this.getOptionValue(option);
    this.onChange(controlValue);
    
    this.showDropdown = false;
    this.highlightedIndex = -1;
    this.onTouched();
  }

  public onClear(): void {
    this.selectedValue = null;
    this.displayValue = '';
    this.searchTerm = '';
    this.highlightedIndex = -1;
    this.clearEvent.emit();
    this.onChange(null);
    this.onTouched();
  }
  
  private getOptionValue(option: SearchOption): any {
    return this.bindValue ? option?.[this.bindValue as keyof SearchOption] : option;
  }

  private getOptionLabel(option: SearchOption): string {
    if (!option) return '';
    const configured = this.bindLabel ? (option as any)[this.bindLabel] : undefined;
    const raw = configured || (option as any).label || (option as any).name || (option as any).description || (option as any).code || '';
    return typeof raw === 'string' ? raw : String(raw);
  }

  public getDisplayText(option: SearchOption): string {
    const labelValue = this.getOptionLabel(option);
    if (this.showCode && option?.code) {
      return `${labelValue} (${option.code})`;
    }
    return labelValue;
  }

  public getRenderedDisplayValue(): string {
    if (!this.suppressGuidDisplay) {
      return this.displayValue;
    }

    const value = (this.displayValue || '').trim();
    const guidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    return guidRegex.test(value) ? '' : this.displayValue;
  }

  public buildDynamicTitle(): string {
    const baseLabel = (this.label || '').replaceAll('*', '').trim();
    if (this.selectedValue) {
      const rawLabel = this.getOptionLabel(this.selectedValue);
      if (rawLabel) {
        return baseLabel ? `${baseLabel}:${rawLabel}` : rawLabel;
      }
      return baseLabel || this.placeholder || '';
    }
    if (this.tooltip?.trim().length) return this.tooltip;
    return baseLabel || this.placeholder || '';
  }

  public isOptionSelected(option: SearchOption): boolean {
    if (!option) return false;
    
    return this.selectedValue ? 
      this.getOptionValue(this.selectedValue) === this.getOptionValue(option) : 
      false;
  }

  public writeValue(value: any): void {
    if (value === null || value === undefined) {
      this.selectedValue = null;
      this.displayValue = '';
      this.searchTerm = '';
      this.highlightedIndex = -1;
      return;
    }
    const foundOption = this.items.find(item => this.getOptionValue(item) === value);
    if (foundOption) {
      this.selectedValue = foundOption;
      const rawLabel = this.getOptionLabel(foundOption);
      const baseLabel = (this.label || '').replaceAll('*', '').trim();
     if (this.prefixLabelOnSelection && baseLabel && rawLabel) {
        this.displayValue = `${baseLabel}:${rawLabel}`;
      } else {
        this.displayValue = rawLabel;
      }
    } else {
      this.selectedValue = null;
      this.displayValue = '';
    }
    this.searchTerm = this.showAsDropdown ? '' : this.displayValue;
    this.highlightedIndex = -1;
  }

  public registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}